import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  Send
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-square/artifacts/7zqhhxkv_squre%20trigger%20black%20%282%29.png";

const footerLinks = {
  Services: [
    { name: 'Website Development', path: '/services/website-development' },
    { name: 'Custom Tool Creation', path: '/services/custom-tool-creation' },
    { name: 'SEO Services', path: '/services/seo-services' },
    { name: 'Social Media Marketing', path: '/services/social-media-marketing' },
    { name: 'UI / UX Design', path: '/services/ui-ux-design' },
    { name: 'E-Commerce Solutions', path: '/services/e-commerce-solutions' }
  ],
  Company: [
    { name: 'About Us', path: '/company/about' },
    { name: 'Our Process', path: '/company/process' },
    { name: 'Our Values', path: '/company/values' },
    { name: 'Careers', path: '/company/careers' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' }
  ],
  Resources: [
    { name: 'Case Studies', path: '/portfolio/case-studies' },
    { name: 'Free Resources', path: '/resources/free' },
    { name: 'FAQs', path: '/resources/faqs' },
    { name: 'News / Press', path: '/resources/news' },
    { name: 'Client Testimonials', path: '/portfolio/testimonials' }
  ]
};

const socialLinks = [
  { name: 'Facebook', icon: Facebook, url: '#' },
  { name: 'Twitter', icon: Twitter, url: '#' },
  { name: 'Instagram', icon: Instagram, url: '#' },
  { name: 'LinkedIn', icon: Linkedin, url: '#' },
  { name: 'YouTube', icon: Youtube, url: '#' }
];

export const Footer = () => {
  const [email, setEmail] = React.useState('');

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    // Handle newsletter subscription
    alert('Thank you for subscribing!');
    setEmail('');
  };

  return (
    <footer className="bg-foreground text-background relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-primary blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-secondary blur-3xl"></div>
      </div>

      <div className="container-custom relative z-10">
        {/* Newsletter Section */}
        <div className="border-b border-background/10 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl font-display font-bold mb-4"
            >
              Stay Ahead with Digital Insights
            </motion.h3>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-background/70 mb-8"
            >
              Subscribe to our newsletter for the latest trends, tips, and insights in digital marketing and web development.
            </motion.p>
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-background/10 border-background/20 text-background placeholder:text-background/50 focus:border-primary"
                required
              />
              <Button variant="gradient" type="submit" className="shrink-0">
                Subscribe
                <Send className="h-4 w-4 ml-2" />
              </Button>
            </motion.form>
          </div>
        </div>

        {/* Main Footer */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <img src={LOGO_URL} alt="Square Trigger" className="h-12 w-auto brightness-0 invert" />
            </Link>
            <p className="text-background/70 mb-6 max-w-sm leading-relaxed">
              Your Digital Success Partner. We help businesses transform their digital presence with innovative solutions, strategic marketing, and cutting-edge technology.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <a href="mailto:info@squaretrigger.com" className="flex items-center gap-3 text-background/70 hover:text-background transition-colors">
                <Mail className="h-4 w-4" />
                <span>info@squaretrigger.com</span>
              </a>
              <a href="tel:+1234567890" className="flex items-center gap-3 text-background/70 hover:text-background transition-colors">
                <Phone className="h-4 w-4" />
                <span>+1 (236) 869-3335</span>
              </a>
              <div className="flex items-start gap-3 text-background/70">
                <MapPin className="h-4 w-4 mt-1" />
                <span>1655 Haro Street, Vancouver, B.C (V6G1G9)</span>
              </div>
            </div>
          </div>

          {/* Links Columns */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 className="font-display font-semibold text-lg mb-6">{category}</h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className="text-background/70 hover:text-background transition-colors animated-underline inline-block"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-background/10 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-background/60 text-sm">
              © {new Date().getFullYear()} Square Trigger. All rights reserved.
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <motion.a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg bg-background/10 text-background/70 hover:bg-primary hover:text-primary-foreground transition-all duration-300"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label={social.name}
                  >
                    <Icon className="h-4 w-4" />
                  </motion.a>
                );
              })}
            </div>

            {/* Legal Links */}
            <div className="flex items-center gap-6 text-sm">
              <Link to="/privacy" className="text-background/60 hover:text-background transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-background/60 hover:text-background transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Schema JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Square Trigger",
          "description": "Your Digital Success Partner - Web Development, SEO, Social Media Marketing, and Digital Solutions",
          "url": "https://squaretrigger.com",
          "logo": LOGO_URL,
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+1-234-567-890",
            "contactType": "customer service",
            "email": "info@squaretrigger.com"
          },
          "sameAs": [
            "https://facebook.com/squaretrigger",
            "https://twitter.com/squaretrigger",
            "https://instagram.com/squaretrigger",
            "https://linkedin.com/company/squaretrigger"
          ]
        })}
      </script>
    </footer>
  );
};
